import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  AccountBalance as FinanceIcon,
  LocalShipping as ShippingIcon,
  CalendarToday as CalendarIcon,
  ConfirmationNumber as InvoiceIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';
import { Customer } from '@/models/customer';
import { CreateInvoiceData } from '@/models/invoice';
import { CompanyConfig } from '@/models/config';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import './InvoiceReview.css';

export interface InvoiceReviewProps {
  formData: CreateInvoiceData;
  selectedCustomer: Customer | null;
  companyConfig: CompanyConfig;
  products: any[]; // Product data for display
  onSave?: () => void;
  onBack?: () => void;
}

const InvoiceReview: React.FC<InvoiceReviewProps> = ({
  formData,
  selectedCustomer,
  companyConfig,
  products,
}) => {
  const [previewDialogOpen, setPreviewDialogOpen] = React.useState(false);
  // Helper function to check if customer is local
  const isLocalCustomer = (customer: Customer | null) => {
    return customer?.localoutside?.toLowerCase() === 'local';
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Get product details for display
  const getProductDetails = (productId: string) => {
    return products.find(p => p._id === productId);
  };

  // Preview handler
  const handlePreview = () => {
    setPreviewDialogOpen(true);
  };

  const handleClosePreviewDialog = () => {
    setPreviewDialogOpen(false);
  };


  return (
    <Box className="invoice-review">
      {/* Header Section */}
      <Box className="review-header">
        <Box className="header-content">
          <Box className="header-left">
            <Typography variant="h4" className="review-title">
              Invoice Review
            </Typography>
            <Typography variant="body1" className="review-subtitle">
              Review all details before saving your invoice
            </Typography>
          </Box>
          <Box className="header-right">
            <Box className="header-actions">
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={handlePreview}
                className="action-button preview-button"
              >
                Preview
              </Button>
              <Chip
                icon={<CheckIcon />}
                label="Ready to Save"
                className="status-chip ready"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3} className="review-content">
        
        {/* Company & Customer Information */}
        <Grid item xs={12} lg={6}>
          <Card className="info-card company-card">
            <CardContent>
              <Box className="card-header">
                <Avatar className="card-avatar company-avatar">
                  <BusinessIcon />
                </Avatar>
                <Box className="card-title-section">
                  <Typography variant="h6" className="card-title">
                    Company Details
                  </Typography>
                  <Typography variant="body2" className="card-subtitle">
                    From
                  </Typography>
                </Box>
              </Box>
              
              <Box className="company-info">
                <Typography variant="h6" className="company-name">
                  {companyConfig.name}
                </Typography>
                <Typography variant="body2" className="company-address">
                  {companyConfig.address}, {companyConfig.city}
                </Typography>
                <Typography variant="body2" className="company-address">
                  {companyConfig.state} {companyConfig.pinCode}, {companyConfig.country}
                </Typography>
                
                <Box className="contact-info">
                  <Typography variant="body2" className="contact-item">
                    📱 {companyConfig.mobile}
                  </Typography>
                  <Typography variant="body2" className="contact-item">
                    ✉️ {companyConfig.email}
                  </Typography>
                </Box>
                
                <Box className="tax-info">
                  <Chip label={`GSTIN: ${companyConfig.gstin}`} size="small" className="tax-chip" />
                  <Chip label={`PAN: ${companyConfig.pan}`} size="small" className="tax-chip" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card className="info-card customer-card">
            <CardContent>
              <Box className="card-header">
                <Avatar className="card-avatar customer-avatar">
                  <PersonIcon />
                </Avatar>
                <Box className="card-title-section">
                  <Typography variant="h6" className="card-title">
                    Customer Details
                  </Typography>
                  <Typography variant="body2" className="card-subtitle">
                    Bill To
                  </Typography>
                </Box>
              </Box>
              
              {selectedCustomer ? (
                <Box className="customer-info">
                  <Typography variant="h6" className="customer-name">
                    {selectedCustomer.name}
                  </Typography>
                  <Typography variant="body2" className="customer-address">
                    {selectedCustomer.address_1}
                    {selectedCustomer.address_2 && `, ${selectedCustomer.address_2}`}
                    {selectedCustomer.address_3 && `, ${selectedCustomer.address_3}`}
                    {selectedCustomer.address_4 && `, ${selectedCustomer.address_4}`}
                  </Typography>
                  <Typography variant="body2" className="customer-address">
                    PIN: {selectedCustomer.pin}
                  </Typography>
                  
                  <Box className="customer-tax-info">
                    {selectedCustomer.gst_id && (
                      <Chip label={`GSTIN: ${selectedCustomer.gst_id}`} size="small" className="tax-chip" />
                    )}
                    {selectedCustomer.panno && (
                      <Chip label={`PAN: ${selectedCustomer.panno}`} size="small" className="tax-chip" />
                    )}
                    <Chip 
                      label={selectedCustomer.localoutside} 
                      size="small"
                      className={`customer-type-chip ${selectedCustomer.localoutside?.toLowerCase()}`}
                    />
                  </Box>
                </Box>
              ) : (
                <Box className="no-customer">
                  <Typography variant="body2" color="text.secondary">
                    No customer selected
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Invoice Details */}
        <Grid item xs={12}>
          <Card className="info-card invoice-details-card">
            <CardContent>
              <Box className="card-header">
                <Avatar className="card-avatar invoice-avatar">
                  <ReceiptIcon />
                </Avatar>
                <Box className="card-title-section">
                  <Typography variant="h6" className="card-title">
                    Invoice Information
                  </Typography>
                  <Typography variant="body2" className="card-subtitle">
                    Document Details
                  </Typography>
                </Box>
              </Box>
              
              <Grid container spacing={3} className="invoice-details-grid">
                <Grid item xs={6} sm={3}>
                  <Box className="detail-item">
                    <Box className="detail-icon">
                      <InvoiceIcon />
                    </Box>
                    <Box className="detail-content">
                      <Typography variant="body2" className="detail-label">
                        Invoice Number
                      </Typography>
                      <Typography variant="h6" className="detail-value">
                        {formData.invoice_number}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={6} sm={3}>
                  <Box className="detail-item">
                    <Box className="detail-icon">
                      <CalendarIcon />
                    </Box>
                    <Box className="detail-content">
                      <Typography variant="body2" className="detail-label">
                        Invoice Date
                      </Typography>
                      <Typography variant="h6" className="detail-value">
                        {formData.invoice_date}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={6} sm={3}>
                  <Box className="detail-item">
                    <Box className="detail-icon">
                      <CalendarIcon />
                    </Box>
                    <Box className="detail-content">
                      <Typography variant="body2" className="detail-label">
                        Order Date
                      </Typography>
                      <Typography variant="h6" className="detail-value">
                        {formData.order_date}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={6} sm={3}>
                  <Box className="detail-item">
                    <Box className="detail-icon">
                      <ShippingIcon />
                    </Box>
                    <Box className="detail-content">
                      <Typography variant="body2" className="detail-label">
                        Transport
                      </Typography>
                      <Typography variant="h6" className="detail-value">
                        {formData.transport || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Products Table */}
        <Grid item xs={12}>
          <Card className="info-card products-card">
            <CardContent>
              <Box className="card-header">
                <Avatar className="card-avatar products-avatar">
                  <InventoryIcon />
                </Avatar>
                <Box className="card-title-section">
                  <Typography variant="h6" className="card-title">
                    Products & Services
                  </Typography>
                  <Typography variant="body2" className="card-subtitle">
                    {formData.products.length} items
                  </Typography>
                </Box>
              </Box>
              
              <TableContainer component={Paper} className="products-table">
                <Table>
                  <TableHead>
                    <TableRow className="table-header">
                      <TableCell className="header-cell">Product</TableCell>
                      <TableCell className="header-cell">HSN Code</TableCell>
                      <TableCell className="header-cell" align="right">Quantity</TableCell>
                      <TableCell className="header-cell" align="right">Rate</TableCell>
                      <TableCell className="header-cell" align="right">Total</TableCell>
                      <TableCell className="header-cell" align="right">GST %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.products.map((productInfo, index) => {
                      const product = getProductDetails(productInfo.product);
                      return (
                        <TableRow key={index} className="table-row">
                          <TableCell className="product-cell">
                            <Box className="product-info">
                              <Typography variant="body2" className="product-name">
                                {product?.item_name || `Product ${index + 1}`}
                              </Typography>
                              <Typography variant="caption" className="product-code">
                                {product?.item_code || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell className="hsn-cell">
                            {product?.hsn_code || 'N/A'}
                          </TableCell>
                          <TableCell align="right" className="quantity-cell">
                            {productInfo.quantity}
                          </TableCell>
                          <TableCell align="right" className="rate-cell">
                            {formatCurrency(product?.rate || 0)}
                          </TableCell>
                          <TableCell align="right" className="total-cell">
                            {formatCurrency((product?.rate || 0) * productInfo.quantity)}
                          </TableCell>
                          <TableCell align="right" className="gst-cell">
                            {product?.gst_percentage || 0}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* HSN Summary Table */}
        {formData.hsn_list.length > 0 && (
          <Grid item xs={12}>
            <Card className="info-card hsn-card">
              <CardContent>
                <Box className="card-header">
                  <Avatar className="card-avatar hsn-avatar">
                    <TrendingIcon />
                  </Avatar>
                  <Box className="card-title-section">
                    <Typography variant="h6" className="card-title">
                      HSN Code Summary
                    </Typography>
                    <Typography variant="body2" className="card-subtitle">
                      Tax breakdown by HSN codes
                    </Typography>
                  </Box>
                </Box>
                
                <TableContainer component={Paper} className="hsn-table">
                  <Table>
                    <TableHead>
                      <TableRow className="table-header">
                        <TableCell className="header-cell">HSN Code</TableCell>
                        <TableCell className="header-cell" align="right">Total Amount</TableCell>
                        {isLocalCustomer(selectedCustomer) ? (
                          <>
                            <TableCell className="header-cell" align="right">CGST</TableCell>
                            <TableCell className="header-cell" align="right">SGST</TableCell>
                          </>
                        ) : (
                          <TableCell className="header-cell" align="right">IGST</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.hsn_list.map((hsn, index) => (
                        <TableRow key={index} className="table-row">
                          <TableCell className="hsn-code-cell">
                            {hsn.hsn_code}
                          </TableCell>
                          <TableCell align="right" className="hsn-amount-cell">
                            {formatCurrency(hsn.total_amount)}
                          </TableCell>
                          {isLocalCustomer(selectedCustomer) ? (
                            <>
                              <TableCell align="right" className="hsn-tax-cell">
                                {formatCurrency(hsn.cgst)}
                              </TableCell>
                              <TableCell align="right" className="hsn-tax-cell">
                                {formatCurrency(hsn.sgst)}
                              </TableCell>
                            </>
                          ) : (
                            <TableCell align="right" className="hsn-tax-cell">
                              {formatCurrency(hsn.igst)}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Financial Summary */}
        <Grid item xs={12}>
          <Card className="info-card financial-card">
            <CardContent>
              <Box className="card-header">
                <Avatar className="card-avatar financial-avatar">
                  <FinanceIcon />
                </Avatar>
                <Box className="card-title-section">
                  <Typography variant="h6" className="card-title">
                    Financial Summary
                  </Typography>
                  <Typography variant="body2" className="card-subtitle">
                    Complete breakdown
                  </Typography>
                </Box>
              </Box>
              
              <Box className="financial-content">
                <Grid container spacing={4}>
                  {/* Left Column - Basic Calculations */}
                  <Grid item xs={12} lg={6}>
                    <Box className="calculation-section">
                      <Typography variant="h6" className="section-title">
                        Basic Calculations
                      </Typography>
                      
                      <Box className="calculation-item">
                        <Typography variant="body2" className="calc-label">
                          Bill Amount (Gross Total)
                        </Typography>
                        <Typography variant="h6" className="calc-value">
                          {formatCurrency(formData.bill_amount)}
                        </Typography>
                      </Box>
                      
                      <Box className="calculation-item">
                        <Typography variant="body2" className="calc-label">
                          Discount ({formData.discount}%)
                        </Typography>
                        <Typography variant="h6" className="calc-value discount">
                          -{formatCurrency((formData.bill_amount * formData.discount) / 100)}
                        </Typography>
                      </Box>
                      
                      <Box className="calculation-item">
                        <Typography variant="body2" className="calc-label">
                          Gross Amount
                        </Typography>
                        <Typography variant="h6" className="calc-value">
                          {formatCurrency(formData.gross_amount)}
                        </Typography>
                      </Box>
                      
                      <Box className="calculation-item">
                        <Typography variant="body2" className="calc-label">
                          Packing Charges
                        </Typography>
                        <Typography variant="h6" className="calc-value">
                          {formatCurrency(formData.packing_charges)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Right Column - GST Breakdown */}
                  <Grid item xs={12} lg={6}>
                    <Box className="gst-section">
                      <Typography variant="h6" className="section-title">
                        GST Breakdown ({isLocalCustomer(selectedCustomer) ? 'CGST + SGST' : 'IGST'})
                      </Typography>
                      
                      {isLocalCustomer(selectedCustomer) ? (
                        <>
                          <Box className="calculation-item">
                            <Typography variant="body2" className="calc-label">
                              CGST
                            </Typography>
                            <Typography variant="h6" className="calc-value gst">
                              {formatCurrency(formData.cgst)}
                            </Typography>
                          </Box>
                          <Box className="calculation-item">
                            <Typography variant="body2" className="calc-label">
                              SGST
                            </Typography>
                            <Typography variant="h6" className="calc-value gst">
                              {formatCurrency(formData.sgst)}
                            </Typography>
                          </Box>
                          <Box className="calculation-item">
                            <Typography variant="body2" className="calc-label">
                              IGST
                            </Typography>
                            <Typography variant="h6" className="calc-value gst">
                              ₹0.00
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Box className="calculation-item">
                            <Typography variant="body2" className="calc-label">
                              CGST
                            </Typography>
                            <Typography variant="h6" className="calc-value gst">
                              ₹0.00
                            </Typography>
                          </Box>
                          <Box className="calculation-item">
                            <Typography variant="body2" className="calc-label">
                              SGST
                            </Typography>
                            <Typography variant="h6" className="calc-value gst">
                              ₹0.00
                            </Typography>
                          </Box>
                          <Box className="calculation-item">
                            <Typography variant="body2" className="calc-label">
                              IGST
                            </Typography>
                            <Typography variant="h6" className="calc-value gst">
                              {formatCurrency(formData.igst)}
                            </Typography>
                          </Box>
                        </>
                      )}
                      
                      <Box className="calculation-item">
                        <Typography variant="body2" className="calc-label">
                          Round Off
                        </Typography>
                        <Typography variant="h6" className="calc-value roundoff">
                          {formatCurrency(formData.round_off)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                
                <Divider className="final-divider" />
                
                {/* Final Total */}
                <Box className="final-total-section">
                  <Typography variant="h4" className="final-total-label">
                    Final Total Amount
                  </Typography>
                  <Typography variant="h3" className="final-total-value">
                    {formatCurrency(formData.total_amount)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreviewDialog}
        maxWidth="lg"
        fullWidth
        className="preview-dialog"
      >
        <DialogTitle className="preview-dialog-title">
          Invoice Preview
        </DialogTitle>
        <DialogContent className="preview-dialog-content">
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" color="primary" gutterBottom>
              Invoice Preview
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Invoice preview functionality will be available in a future update.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreviewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceReview; 